
import {RefObject, useEffect, useRef} from 'react';
import {Routes} from 'constants/index';
import {isAppWebView} from 'utils/appWebview';
import {hasWindow} from 'utils/functions';

export type CoordsCallback = (
	coords: {
		lat: number,
		lng: number,
	},
	source: 'GPS' | 'IP',
) => void

// Routes that never prompt for browser location. See useUserCoords.
const noGeolocationRoutes = [
	Routes.appviewDexa,
	Routes.signIn,
	Routes.signUp,
	Routes.resetPassword,
	Routes.resetSuccess,
	Routes.changePassword,
];

export const isNoGeolocationRoute = (path: string): boolean => (
	noGeolocationRoutes.some((route) => {
		// Some Routes carry a trailing slash, some don't; strip it so
		// the subpath test below can't build a `//` that never matches
		const base = route.replace(/\/$/, '');
		return path === base || path.startsWith(`${base}/`);
	})
);

const geoTarget = 'https://static.bodyspec.com/api/geo';
const geoIPAfter = 10000; // 10 seconds - falls back to IP if user doesn't respond

type GeoData = {
	city: string
	region: string
	country: string
	lat: string
	lng: string
	postal: string
	timezone: string
};

// IP-based geolocation fallback
const fetchIPCoords = async (cb: CoordsCallback) => {
	try {
		const result = await fetch(geoTarget);
		const obj: GeoData = await result.json();

		// API returns null when location can't be determined;
		// Number(null) = 0 and isFinite(0) = true, so nulls fall through to 0,0
		const lat = Number(obj.lat);
		const lng = Number(obj.lng);

		cb({
			lat: isFinite(lat) ? lat : 0,
			lng: isFinite(lng) ? lng : 0,
		}, 'IP');
	} catch {
		// Network error or CORS blocked - call back with 0,0
		cb({lat: 0, lng: 0}, 'IP');
	}
};

export const useUserCoords = (
	cb?: CoordsCallback,
	skipRef?: RefObject<boolean>,
	options: {
		// Look up the IP location right away instead of waiting on the
		// browser prompt; GPS still refines it if the visitor allows it
		ipNow?: boolean
	} = {},
) => {
	const cbRef = useRef(cb);
	cbRef.current = cb;

	useEffect(() => {
		const invoke: CoordsCallback = (...args) => cbRef.current?.(...args);

		// Caller asked to skip geolocation entirely (e.g. embedded
		// iframes that have no use for region/coords). Don't prompt
		// and don't fall back to IP — leaves geoCoords unset.
		if (skipRef?.current) {
			return;
		}

		// Resolve via IP (never a browser prompt) when:
		//  - the geolocation API is missing, or
		//  - we're inside the app WebView (native privacy label
		//    forbids the prompt), or
		//  - we're on a no-geolocation route. These are the cases the
		//    UA marker can't cover: the app report route serves builds
		//    predating the marker (< 0.1.0+32), and the app's login
		//    runs in Apple's auth session, whose User-Agent the app
		//    can't tag - the Keycloak theme links from there to
		//    /sign-up and /reset-password. Nothing on these pages
		//    needs location, so the skip is unconditional.
		// Either way region/coords still populate via IP.
		const path = hasWindow() ? window.location.pathname : '';
		if (
			typeof navigator === 'undefined' ||
			!navigator.geolocation ||
			isAppWebView() ||
			isNoGeolocationRoute(path)
		) {
			fetchIPCoords(invoke);
			return;
		}

		let ipResolved = false;
		if (options.ipNow) {
			ipResolved = true;
			fetchIPCoords(invoke);
		}

		navigator.geolocation.getCurrentPosition(
			// If we're able to get coords from browser API, great!
			// This can fire even after IP fallback - GPS overwrites IP
			(position) => {
				invoke({
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				}, 'GPS');
			},

			// If we fail, do GeoIP lookup and parse
			() => {
				if (!ipResolved) {
					ipResolved = true;
					fetchIPCoords(invoke);
				}
			},
		);

		// Timeout fallback - if user doesn't respond in time, use IP
		setTimeout(() => {
			if (!ipResolved) {
				ipResolved = true;
				fetchIPCoords(invoke);
			}
		}, geoIPAfter);
	}, []);
};
